/**
 * Medication Reminder Service
 * 
 * Handles scheduling and managing daily medication reminders using
 * native local notifications. Works with the existing notification service.
 * 
 * Features:
 * - Schedule daily reminders at specific times
 * - Support multiple doses per day
 * - Track medication adherence
 * - Snooze reminders
 * - Web fallback for browser testing
 */

import { Capacitor } from '@capacitor/core';
import nativeNotificationService, {
  scheduleAppointmentReminder,
  cancelNotification,
  showNotification,
  requestNotificationPermission,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_TYPE,
} from './nativeNotificationService';

// Dynamic import for local notifications
let LocalNotifications = null;

// Reminder storage key
const MEDICATION_REMINDERS_KEY = 'medication_reminders';

/**
 * Initialize local notifications plugin
 */
const initPlugin = async () => {
  if (LocalNotifications) return true;
  
  if (!Capacitor.isNativePlatform()) {
    return false;
  }
  
  try {
    const module = await import('@capacitor/local-notifications');
    LocalNotifications = module.LocalNotifications;
    return true;
  } catch (error) {
    console.error('[MedReminder] Failed to load plugin:', error);
    return false;
  }
};

/**
 * Parse time string (HH:MM) to hours and minutes
 */
const parseTime = (timeStr) => {
  if (!timeStr) return { hours: 8, minutes: 0 };
  const [hours, minutes] = timeStr.split(':').map(Number);
  return { hours: hours || 8, minutes: minutes || 0 };
};

/**
 * Generate unique notification ID from medication ID and time
 */
const generateNotificationId = (medicationId, timeSlot) => {
  const str = `${medicationId}_${timeSlot}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

/**
 * Schedule a daily medication reminder
 * 
 * @param {Object} medication - Medication details
 * @param {string} medication.id - Unique medication ID
 * @param {string} medication.name - Medication name
 * @param {string} medication.dosage - Dosage (e.g., "500mg")
 * @param {string[]} medication.reminderTimes - Array of times ["08:00", "20:00"]
 * @returns {Object} - Result with scheduled notification IDs
 */
export const scheduleMedicationReminder = async (medication) => {
  const {
    id,
    name,
    dosage,
    reminderTimes = ['08:00'], // Default to 8 AM
    instructions,
  } = medication;
  
  if (!id || !name) {
    return { success: false, error: 'Invalid medication data' };
  }
  
  const isNative = await initPlugin();
  const { granted } = await requestNotificationPermission();
  
  if (!granted) {
    return { success: false, error: 'Notification permission denied' };
  }
  
  const scheduledIds = [];
  const errors = [];
  
  for (const timeSlot of reminderTimes) {
    const notificationId = generateNotificationId(id, timeSlot);
    const { hours, minutes } = parseTime(timeSlot);
    
    // Build notification content (SECURITY: Only show medication name, not detailed health info)
    const title = 'Medication Reminder';
    const body = `Time to take ${name}${dosage ? ` (${dosage})` : ''}`;
    
    if (isNative && LocalNotifications) {
      try {
        // Schedule repeating daily notification
        await LocalNotifications.schedule({
          notifications: [{
            id: notificationId,
            title,
            body,
            schedule: {
              on: {
                hour: hours,
                minute: minutes,
              },
              repeats: true,
              allowWhileIdle: true,
            },
            channelId: NOTIFICATION_CHANNELS.REMINDERS,
            smallIcon: 'ic_stat_notification',
            largeIcon: 'ic_launcher',
            actionTypeId: 'MEDICATION_ACTION',
            extra: {
              type: NOTIFICATION_TYPE.MEDICATION_REMINDER,
              medicationId: id,
              medicationName: name,
              timeSlot,
              url: '/medical/medications',
            },
            sound: 'default',
            group: 'medications',
          }],
        });
        
        scheduledIds.push({ id: notificationId, time: timeSlot });
        console.log(`[MedReminder] Scheduled ${name} at ${timeSlot}`);
      } catch (error) {
        console.error(`[MedReminder] Failed to schedule ${timeSlot}:`, error);
        errors.push({ time: timeSlot, error: error.message });
      }
    } else {
      // Web fallback - store in localStorage for manual checking
      const webReminders = JSON.parse(localStorage.getItem(MEDICATION_REMINDERS_KEY) || '[]');
      webReminders.push({
        id: notificationId,
        medicationId: id,
        medicationName: name,
        dosage,
        time: timeSlot,
        instructions,
        createdAt: new Date().toISOString(),
      });
      localStorage.setItem(MEDICATION_REMINDERS_KEY, JSON.stringify(webReminders));
      scheduledIds.push({ id: notificationId, time: timeSlot, webFallback: true });
    }
  }
  
  return {
    success: errors.length === 0,
    scheduledIds,
    errors: errors.length > 0 ? errors : undefined,
  };
};

/**
 * Cancel all reminders for a medication
 */
export const cancelMedicationReminders = async (medicationId) => {
  const isNative = await initPlugin();
  
  if (isNative && LocalNotifications) {
    try {
      const pending = await LocalNotifications.getPending();
      const medNotifs = pending.notifications.filter(n => 
        n.extra?.medicationId === medicationId
      );
      
      if (medNotifs.length > 0) {
        await LocalNotifications.cancel({
          notifications: medNotifs.map(n => ({ id: n.id })),
        });
      }
      
      return { success: true, cancelled: medNotifs.length };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
  
  // Web fallback
  try {
    const webReminders = JSON.parse(localStorage.getItem(MEDICATION_REMINDERS_KEY) || '[]');
    const filtered = webReminders.filter(r => r.medicationId !== medicationId);
    const cancelled = webReminders.length - filtered.length;
    localStorage.setItem(MEDICATION_REMINDERS_KEY, JSON.stringify(filtered));
    return { success: true, cancelled };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Update reminders for a medication (cancel old, schedule new)
 */
export const updateMedicationReminders = async (medication) => {
  await cancelMedicationReminders(medication.id);
  return scheduleMedicationReminder(medication);
};

/**
 * Get all scheduled medication reminders
 */
export const getMedicationReminders = async () => {
  const isNative = await initPlugin();
  
  if (isNative && LocalNotifications) {
    try {
      const pending = await LocalNotifications.getPending();
      return pending.notifications.filter(n => 
        n.extra?.type === NOTIFICATION_TYPE.MEDICATION_REMINDER
      );
    } catch (error) {
      console.error('[MedReminder] Failed to get reminders:', error);
      return [];
    }
  }
  
  // Web fallback
  return JSON.parse(localStorage.getItem(MEDICATION_REMINDERS_KEY) || '[]');
};

/**
 * Snooze a medication reminder (reschedule for later)
 * 
 * @param {number} notificationId - The notification ID
 * @param {number} snoozeMinutes - Minutes to snooze (default 15)
 */
export const snoozeMedicationReminder = async (notificationId, snoozeMinutes = 15) => {
  const isNative = await initPlugin();
  
  if (!isNative || !LocalNotifications) {
    return { success: false, error: 'Not available in web' };
  }
  
  try {
    // Get the original notification
    const pending = await LocalNotifications.getPending();
    const original = pending.notifications.find(n => n.id === notificationId);
    
    if (!original) {
      return { success: false, error: 'Notification not found' };
    }
    
    // Schedule a one-time snooze notification
    const snoozeId = notificationId + 1000000; // Offset ID for snooze
    const snoozeTime = new Date(Date.now() + snoozeMinutes * 60 * 1000);
    
    await LocalNotifications.schedule({
      notifications: [{
        id: snoozeId,
        title: original.title,
        body: `${original.body} (Snoozed)`,
        schedule: {
          at: snoozeTime,
        },
        channelId: NOTIFICATION_CHANNELS.REMINDERS,
        extra: original.extra,
        sound: 'default',
      }],
    });
    
    return { success: true, snoozeUntil: snoozeTime.toISOString() };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Mark medication as taken (for adherence tracking)
 * 
 * @param {string} medicationId - Medication ID
 * @param {string} timeSlot - The time slot taken (e.g., "08:00")
 */
export const markMedicationTaken = async (medicationId, timeSlot) => {
  const key = `med_taken_${new Date().toISOString().split('T')[0]}`;
  const taken = JSON.parse(localStorage.getItem(key) || '{}');
  
  if (!taken[medicationId]) {
    taken[medicationId] = [];
  }
  
  if (!taken[medicationId].includes(timeSlot)) {
    taken[medicationId].push(timeSlot);
  }
  
  localStorage.setItem(key, JSON.stringify(taken));
  
  return { success: true, takenAt: new Date().toISOString() };
};

/**
 * Get medication adherence for a date
 * 
 * @param {string} date - Date in YYYY-MM-DD format (default today)
 */
export const getMedicationAdherence = (date = null) => {
  const targetDate = date || new Date().toISOString().split('T')[0];
  const key = `med_taken_${targetDate}`;
  return JSON.parse(localStorage.getItem(key) || '{}');
};

/**
 * Preset reminder schedules
 */
export const REMINDER_PRESETS = {
  ONCE_DAILY_MORNING: ['08:00'],
  ONCE_DAILY_EVENING: ['20:00'],
  TWICE_DAILY: ['08:00', '20:00'],
  THREE_TIMES_DAILY: ['08:00', '14:00', '20:00'],
  FOUR_TIMES_DAILY: ['08:00', '12:00', '16:00', '20:00'],
  WITH_MEALS: ['08:00', '12:30', '18:30'],
  BEFORE_BED: ['22:00'],
};

export default {
  scheduleMedicationReminder,
  cancelMedicationReminders,
  updateMedicationReminders,
  getMedicationReminders,
  snoozeMedicationReminder,
  markMedicationTaken,
  getMedicationAdherence,
  REMINDER_PRESETS,
};
